"use client";

import { type ReactNode, useState } from "react";
import { m } from "framer-motion";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import dynamic from "next/dynamic";

import type { Header as HeaderData } from "@/lib/validations/cms";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, categorySlug } from "@/lib/shared/utils";
import EditableText from "@/components/edit-mode/EditableText";

import BrandMark from "./header/BrandMark";
import HeaderTopBar from "./header/HeaderTopBar";
import { SearchToggle, useKeyboardShortcuts } from "./header/HeaderSearchToggle";
import HeaderMenu, { MenuToggle } from "./header/HeaderMenu";
import LiveCart from "./header/LiveCart";
import { useHeaderMotion } from "./header/useHeaderMode";
import { useEditMode } from "@/contexts/EditModeContext";

const HeaderSearch = dynamic(() => import("./header/HeaderSearch"), { ssr: false });
const UserLink = dynamic(() => import("./header/UserLink"), { ssr: false });
const MENU_NAV_HEIGHT = 44;
const TOP_BAR_HEIGHT = 20;

type HeaderProps = {
  data: HeaderData;
  cartSlot: ReactNode;
};

export default function Header({ data, cartSlot }: HeaderProps) {
  const isMobile = useIsMobile();
  const { isEditing } = useEditMode();

  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const categories = data.categories || [];

  const openSearch = () => {
    setMenuOpen(false);
    setSearchOpen(true);
  };
  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false);
    } else {
      openSearch();
    }
  };
  const toggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false);
    } else {
      setSearchOpen(false);
      setMenuOpen(true);
    }
  };

  const { navHeight, topBarHeight, topBarOpacity, stripHeight, stripOpacity, compact } =
    useHeaderMotion();

  const menuCompact = menuOpen || compact;

  useKeyboardShortcuts(() => openSearch());

  const categoryLinkClass =
    "text-foreground/60 hover:text-foreground group inline-flex cursor-pointer items-center gap-1 font-mono text-[10px] leading-none font-bold tracking-[0.2em] uppercase transition-colors";

  return (
    <>
      <a
        href="#main-content"
        className="bg-foreground text-background focus-visible:ring-foreground focus-visible:ring-offset-background sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:left-2 focus-visible:z-[100] focus-visible:px-3 focus-visible:py-2 focus-visible:text-xs focus-visible:font-bold focus-visible:tracking-[0.2em] focus-visible:uppercase focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        Skip to content
      </a>

      <header
        data-header
        data-mode={menuOpen ? "menuOpen" : compact ? "scrolled" : "atTop"}
        className={cn(
          "bg-background/85 text-foreground supports-[backdrop-filter]:bg-background/70 z-[100] border-b backdrop-blur-xl",
          "border-foreground/10",
          menuOpen ? "fixed inset-x-0 top-0" : "sticky top-0"
        )}
        role="banner"
      >
        <m.div
          style={{ height: menuOpen ? 0 : topBarHeight, opacity: menuOpen ? 0 : topBarOpacity }}
          className="overflow-hidden"
          aria-hidden={menuOpen || topBarHeight.get() === 0}
        >
          <HeaderTopBar data={data} />
        </m.div>

        {/* Search overlay — always mounted, portal-based, manages own visibility */}
        <HeaderSearch
          open={searchOpen}
          onOpenChange={setSearchOpen}
          compact={compact}
          categories={categories}
        />

        <m.nav
          style={{ height: menuOpen ? MENU_NAV_HEIGHT : navHeight }}
          className="relative z-10 mx-auto flex max-w-[1920px] items-center overflow-hidden px-4 transition-[height] duration-300 ease-out sm:px-6 lg:px-8"
          aria-label="Primary"
        >
          <div className="flex flex-1 items-center gap-2">
            <MenuToggle
              open={menuOpen}
              onToggle={() => toggleMenu()}
              compact={menuCompact}
              label={data.menuText}
            />
          </div>

          <div className="absolute left-1/2 -translate-x-1/2">
            <BrandMark logo={data.logo} compact={menuCompact} />
          </div>

          <div className="flex flex-1 items-center justify-end gap-2 sm:gap-3">
            {/* Search toggle — shown on all screen sizes */}
            <SearchToggle
              open={searchOpen}
              onToggle={() => toggleSearch()}
              compact={menuCompact}
              label={data.searchText}
            />
            {!isMobile && <UserLink compact={menuCompact} accountText={data.accountText} />}

            <LiveCart compact={menuCompact} />
          </div>
        </m.nav>

        {!isMobile && !menuOpen && data.categories.length > 0 && (
          <m.div
            style={{ height: stripHeight, opacity: stripOpacity }}
            className="border-foreground/10 overflow-hidden border-t"
            aria-hidden={stripHeight.get() === 0}
          >
            <div className="mx-auto flex h-full max-w-[1920px] items-center justify-between px-4 sm:px-6 lg:px-8">
              <ul className="flex items-center gap-6 overflow-x-auto">
                {data.categories.slice(0, 10).map((category) => {
                  const slug = categorySlug(category);
                  return (
                    <li key={category} className="shrink-0">
                      <Link
                        href={`/products/category/${slug}`}
                        className={categoryLinkClass}
                        onClick={(e) => {
                          if (isEditing) e.preventDefault();
                        }}
                      >
                        <EditableText section="header" field="categories" value={category} />
                        <ChevronRight
                          aria-hidden
                          size={10}
                          strokeWidth={2}
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="hidden shrink-0 items-center gap-6 sm:flex">
                <Link
                  href="/drops"
                  className={categoryLinkClass}
                  onClick={(e) => {
                    if (isEditing) e.preventDefault();
                  }}
                >
                  Drops
                  <ChevronRight size={10} strokeWidth={2} />
                </Link>
                <Link
                  href="/products"
                  className={categoryLinkClass}
                  onClick={(e) => {
                    if (isEditing) e.preventDefault();
                  }}
                >
                  <EditableText section="header" field="menuText" value="All" />{" "}
                  <ChevronRight size={10} strokeWidth={2} />
                </Link>
              </div>
            </div>
          </m.div>
        )}
      </header>

      <HeaderMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        onOpenSearch={openSearch}
        data={data}
        topOffset={menuCompact ? MENU_NAV_HEIGHT : TOP_BAR_HEIGHT + MENU_NAV_HEIGHT}
      />

      <div data-cart-host className="contents" aria-hidden>
        {cartSlot}
      </div>
    </>
  );
}
